import React from "react";
import FolieLayout from "./FolieLayout.jsx";
import HeaderWithHelp from "../../SalesAnalysis/HeaderWithHelp.jsx";

/**
 * Folie 1 "Woher die Zahlen kommen" (Auftrag #58, Wortlaut Thorsten
 * 29.09.2026: „direkt am Anfang eine Folie mit der Zusammenfassung aller
 * Quellen … Aber bitte verständlich“): die verdichtete Fassung der
 * Quellen-Tabelle weiter unten - welche Quellen laufen von selbst, welche
 * kommen von Hand, jeweils mit Takt und letztem Stand. Die Stände kommen
 * live aus denselben Kennzahlen wie die Tabelle (DatenquellenQuery), nichts
 * ist fest hinterlegt.
 */

const AUTOMATISCH = [
    {schluessel: "bestand_snv", name: "Lagerbestand SNV Weilheim", was: "was physisch im Hauptlager liegt", quelle: "weclapp"},
    {schluessel: "bestand_lahr", name: "Lagerbestand Lahr", was: "FLAME seit 22.09. in der Rechnung", quelle: "weclapp"},
    {schluessel: "offene_kundenmenge", name: "Offen, nicht reserviert (Planung)", was: "verkauft, noch nicht ausgeliefert", quelle: "weclapp", takt: "stündlich"},
    {schluessel: "verkaeufe", name: "Eigene Verkäufe", was: "Grundlage für Wochenbedarf und Saison", quelle: "weclapp (bis 30.06.2026 Alt-ERP)", takt: "nächtlich um 05:37"},
    {schluessel: "b2b_nachfrage", name: "Nachfrage aus dem B2B-Shop", was: "Warenkörbe und Suchen ohne Treffer", quelle: "B2B-Shop b2b.molotow.com"},
    {schluessel: "tage_ohne_bestand", name: "Bestandsverlauf", was: "Tagesstand je Lager", quelle: "eigene Fortschreibung im Ordermanagement"},
];

const EINGELESEN = [
    {
        schluessel: "ampel",
        name: "Zusagen von Cosmos (Ampel-Datei)",
        was: "welche Menge für welche Woche zugesagt ist",
        quelle: "Excel-Datei von Cosmos per Mail",
        takt: "bei jeder neuen Datei",
        automatisierung: "über die von Cosmos angekündigte Schnittstelle",
    },
    {
        schluessel: "cosmos_puffer",
        name: "Cosmos-Pufferbestand",
        was: "nur Anhaltspunkt, nicht in der Prognose – brutto, enthält offene Bestellungen",
        quelle: "Excel-Liste „Cosmos Stock Overview“ von Cosmos per Mail",
        takt: "bei jeder neuen Liste (derzeit monatlich)",
        automatisierung: "über die von Cosmos angekündigte Schnittstelle",
    },
    {
        schluessel: "liefergelegenheiten",
        name: "Liefergelegenheiten",
        was: "die Wochen, in denen Cosmos anliefert",
        quelle: "im Bereich gepflegt – eine Absprache, keine Datei",
        takt: "bei jeder Änderung",
        automatisierung: "keine nötig – es sind Absprachen, keine Daten",
    },
    {
        schluessel: null,
        name: "Oskars Bestellrechner",
        was: "Vergleichsmaßstab, nicht in der Rechnung",
        quelle: "Excel-Datei aus dem Haus",
        takt: "bei jeder neuen Datei",
    },
    {
        schluessel: "snv_warenbewegung",
        name: "Warenbewegungen von Schneider Novus",
        was: "trennt die Verkäufe nach Weilheim und Lahr",
        quelle: "Datei von Schneider Novus per Mail",
        takt: "bei jeder neuen Datei",
        automatisierung: "wiederkehrender Dateiabruf bei Schneider Novus",
    },
];

function standText(iso) {
    if (!iso) {
        return null;
    }
    const datum = new Date(iso);
    if (Number.isNaN(datum.getTime())) {
        return null;
    }
    // Anzeige immer in deutscher Zeit, unabhängig vom Gerät (regel:zeitzone-pruefen).
    const teile = new Intl.DateTimeFormat("de-DE", {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).formatToParts(datum);
    const t = Object.fromEntries(teile.map((p) => [p.type, p.value]));
    const mitternacht = t.hour === "00" && t.minute === "00";
    return mitternacht ? `${t.day}.${t.month}.` : `${t.day}.${t.month}., ${t.hour}:${t.minute} Uhr`;
}

function QuellenGruppe({titel, hilfe, eintraege, jeSchluessel, farbe}) {
    return (
        <div className="border rounded p-3" style={{borderLeft: `4px solid ${farbe}`, background: "#fff"}}>
            <div className="fw-semibold mb-2">
                <HeaderWithHelp help={hilfe}>{titel}</HeaderWithHelp>
            </div>
            <ul className="list-unstyled mb-0">
                {eintraege.map((e) => {
                    const kennzahl = e.schluessel ? jeSchluessel[e.schluessel] : null;
                    const stand = standText(kennzahl?.stand);
                    const takt = e.takt ?? kennzahl?.aktualisierung;
                    const zeitangabe = [takt, stand ? `zuletzt ${stand}` : null].filter(Boolean).join(" · ");
                    return (
                        <li key={e.name} className="py-1 border-top" style={{lineHeight: 1.35}}>
                            <div>
                                <span className="fw-semibold">{e.name}</span>
                                <span className="small text-muted"> – {e.was}</span>
                            </div>
                            <div className="small">
                                <span className="text-muted">Quelle:</span> {e.quelle}
                                {zeitangabe && <span className="text-muted"> · {zeitangabe}</span>}
                            </div>
                            {e.automatisierung && (
                                <div className="small">
                                    <span className="text-muted">Automatisierbar:</span> {e.automatisierung}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default function Folie1Quellen({kennzahlen}) {
    const jeSchluessel = Object.fromEntries((kennzahlen ?? []).map((k) => [k.schluessel, k]));
    const ampelIso = jeSchluessel.ampel?.stand;
    const ampelDatum = ampelIso
        ? new Intl.DateTimeFormat("de-DE", {timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric"}).format(new Date(ampelIso))
        : null;

    return (
        <FolieLayout
            nummer={1}
            breiterText
            titel="Woher die Zahlen kommen"
            hilfe="Alle Quellen des Bereichs auf einen Blick: was sich von selbst aktualisiert und was von Hand hereinkommt, jeweils mit Takt und letztem Stand."
            text={
                <p className="mb-0">
                    Alles rund um den Bestand ist auf die Stunde aktuell und pflegt sich selbst. Alles rund um Cosmos’
                    Zusagen ist so aktuell wie die zuletzt eingelesene Datei
                    {ampelDatum ? ` (derzeit vom ${ampelDatum}).` : "."} Mit Achilles Segos von Cosmos sind wir im
                    Austausch über eine Schnittstelle, die Cosmos zur Verfügung stellen will – angekündigt, aber bis
                    heute ohne zugesagten Termin.
                </p>
            }
            leseHinweis="Links steht, was das System selbst abruft, rechts, was als Datei eingelesen oder im Bereich gepflegt wird. „Zuletzt“ ist der jeweils jüngste Stand im System. Alle Einzelheiten stehen weiter unten unter „Alle Quellen im Detail“."
        >
            <div className="row g-3">
                <div className="col-12 col-lg-6">
                    <QuellenGruppe
                        titel="Läuft von selbst"
                        hilfe="Diese Quellen ruft das System im angegebenen Takt selbst ab – niemand muss etwas tun."
                        eintraege={AUTOMATISCH}
                        jeSchluessel={jeSchluessel}
                        farbe="#198754"
                    />
                </div>
                <div className="col-12 col-lg-6">
                    <QuellenGruppe
                        titel="Wird eingelesen oder gepflegt"
                        hilfe="Diese Dateien kommen per Mail und werden maschinell eingelesen, nicht abgetippt; die Liefergelegenheiten werden im Bereich gepflegt. Sie sind so aktuell wie die zuletzt eingelesene Fassung."
                        eintraege={EINGELESEN}
                        jeSchluessel={jeSchluessel}
                        farbe="#7500EC"
                    />
                    <div className="border rounded p-3 mt-3 small" style={{background: "#fff8e1", borderColor: "#f0c36d"}}>
                        <div className="fw-semibold mb-1">
                            <HeaderWithHelp help="Das Auslesen der Lieferscheine ist ausgearbeitet und an 70 echten Lieferscheinen erprobt. Für den automatischen Betrieb fehlt noch das Leserecht auf das Postfach, in dem sie ankommen.">
                                Vorbereitet, noch nicht scharf: Lieferscheine von Cosmos
                            </HeaderWithHelp>
                        </div>
                        Sie kommen heute per Mail an einzelne Personen und an das Rechnungspostfach. Die automatische
                        Auswertung ist vorbereitet und an 70 Lieferscheinen erprobt – sie muss nur noch scharf
                        geschaltet werden, sobald das Leserecht auf das Postfach eingerichtet ist.
                    </div>
                </div>
            </div>
        </FolieLayout>
    );
}
